import { InvoiceTaxRow } from "@web-src/modules/invoices/types";
import { useState, useCallback, useMemo } from "react";
import { v4 as uuid4 } from "uuid";

const MAX_SELECTED_TAXES = 3;

interface TaxFields {
  item: InvoiceTaxRow[];
  invoice: InvoiceTaxRow[];
}

interface SelectedTaxes {
  item: string[];
  invoice: string[];
}

interface UseTaxFieldsReturn {
  taxFields: TaxFields;
  selectedTaxes: SelectedTaxes;
  handleAddTaxField: (
    level: "item" | "invoice",
    field: InvoiceTaxRow & { selected?: boolean; default?: boolean }
  ) => void;
  handleEditTaxField: (level: "item" | "invoice", field: InvoiceTaxRow) => void;
  handleRemoveTaxField: (level: "item" | "invoice", id: string) => void;
  handleSetDefaultTaxField: (
    level: "item" | "invoice",
    id: string,
    isDefault: boolean
  ) => void;
  handleSetAsSelected: (level: "item" | "invoice", id: string) => boolean;
  handleRemoveFromSelected: (level: "item" | "invoice", id: string) => void;
  setTaxFields: React.Dispatch<React.SetStateAction<TaxFields>>;
  canSelectMoreTaxes: (level: "item" | "invoice") => boolean;
}

export const useTaxFields = (
  initialTaxFields?: TaxFields
): UseTaxFieldsReturn => {
  const [taxFields, setTaxFields] = useState<TaxFields>({
    item: initialTaxFields?.item || [],
    invoice: initialTaxFields?.invoice || [],
  });

  const [selectedTaxes, setSelectedTaxes] = useState<SelectedTaxes>({
    item: [],
    invoice: [],
  });

  const canSelectMoreTaxes = useCallback(
    (level: "item" | "invoice") =>
      selectedTaxes[level].length < MAX_SELECTED_TAXES,
    [selectedTaxes]
  );

  const handleRemoveTaxField = useCallback(
    (level: "item" | "invoice", id: string) => {
      setTaxFields((prev) => ({
        ...prev,
        [level]: prev[level].filter((item) => item.id !== id),
      }));
    },
    []
  );

  const handleSetDefaultTaxField = useCallback(
    (level: "item" | "invoice", id: string, isDefault: boolean) => {
      setTaxFields((prev) => ({
        ...prev,
        [level]: prev[level].map((field) => ({
          ...field,
          default: field.id === id ? isDefault : field.default,
        })),
      }));
    },
    []
  );
  const handleSetAsSelected = useCallback(
    (level: "item" | "invoice", id: string): boolean => {
      if (selectedTaxes[level].includes(id)) {
        return false;
      }

      // if (selectedTaxes[level].length >= MAX_SELECTED_TAXES) {
      //   return false;
      // }

      setSelectedTaxes((prev) => ({
        ...prev,
        [level]: [...new Set([...prev[level], id])],
      }));
      return true;
    },
    [selectedTaxes]
  );

  const handleAddTaxField = useCallback(
    (
      level: "item" | "invoice",
      field: InvoiceTaxRow & { selected?: boolean; default?: boolean }
    ) => {
      const uuid = uuid4();

      setTaxFields((prev) => {
        const existingField = prev[level].find(
          (existing) =>
            existing.name === field.name &&
            existing.percentage === field.percentage
        );

        const newField = {
          ...field,
          id: uuid,
        };

        if (field.selected) {
          handleSetAsSelected(level, newField.id);
        }

        if (existingField) {
          if (field.selected) {
            handleSetAsSelected(level, existingField.id);
          }

          if (field.default) {
            handleSetDefaultTaxField(level, existingField.id, true);
          }

          return prev;
        }

        return {
          ...prev,
          [level]: [...prev[level], newField],
        };
      });
    },
    [handleSetAsSelected, handleSetDefaultTaxField]
  );

  const handleEditTaxField = useCallback(
    (level: "item" | "invoice", newItem: InvoiceTaxRow) => {
      setTaxFields((prev) => ({
        ...prev,
        [level]: prev[level].map((item) =>
          item.id === newItem.id ? newItem : item
        ),
      }));
    },
    []
  );

  const handleRemoveFromSelected = useCallback(
    (level: "item" | "invoice", id: string) => {
      setSelectedTaxes((prev) => ({
        ...prev,
        [level]: prev[level].filter((selectedId) => selectedId !== id),
      }));
    },
    []
  );

  return useMemo(
    () => ({
      taxFields,
      selectedTaxes,
      handleAddTaxField,
      handleEditTaxField,
      handleRemoveTaxField,
      handleSetDefaultTaxField,
      handleSetAsSelected,
      handleRemoveFromSelected,
      setTaxFields,
      canSelectMoreTaxes,
    }),
    [
      taxFields,
      selectedTaxes,
      handleAddTaxField,
      handleEditTaxField,
      handleRemoveTaxField,
      handleSetDefaultTaxField,
      handleSetAsSelected,
      handleRemoveFromSelected,
      canSelectMoreTaxes,
    ]
  );
};
